import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomSelect from '../components/shared/CustomSelect';
import HelpButton from '../components/shared/HelpButton';
import ContractViewer from '../components/ContractViewer';
import { useUser } from '../utils/UserContext';
import { rolesAccess } from '../data/map';
import { cssUrl } from '../utils/cssVersion';

// --- Constants ---
const WEEKDAY_OPTIONS = ['Mon', 'Tues', 'Wed', 'Thur', 'Fri', 'Sat', 'Sun'];

const WORK_TYPE_OPTIONS = [
  'Regular', 'Casual', 'HTP Cleaning', 'Departure Clean',
  'Vacate Clean', 'Supervision', 'Extra Work', 'Public Holiday',
  'Event', 'Monthly Periodical', 'Rectification Periodicals',
  'Emergency callout', 'Training', 'Fill In'
];

const TEAM_LEADER_OPTIONS = [
  'Simon', 'Lauro', 'Tayde Susana', 'Sandra Julieth',
  'Danielle','Laura Sofia','Caroline','Md Mahamudul','Miguel Angel','Tomas'
];

const CHARGEABLE_OPTIONS = ['Yes', 'No'];

// ===== HELPER: Is a YYYY-MM-DD string in a prior month relative to today? =====
const isPriorMonth = (dateStr) => {
  if (!dateStr) return false;
  const [y, m] = dateStr.split('-').map(Number);
  const now = new Date();
  return y < now.getFullYear() || (y === now.getFullYear() && m - 1 < now.getMonth());
};

// ===== MAIN COMPONENT =====
const Extrawork = () => {
  const navigate = useNavigate();
  const hasFetched = useRef(false);

  // --- User & role ---
  const { user } = useUser();
  const isAdmin = rolesAccess.admin.includes(user?.userJob || '');

  const [isLoading, setIsLoading] = useState(true);
  const [cleanersList, setCleanersList] = useState([]);
  const [sitesList, setSitesList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);

  const createEmptyFormSet = () => ({
    id: Date.now(),
    dateWorked: '',
    siteWorking: '',
    cleaner: '',
    dayworked: '',
    typeOfWork: '',
    chargeable: '',
    hoursWorked: '',
    supervisor: '',
  });

  const [formSets, setFormSets] = useState([createEmptyFormSet()]);
  const [touchedSets, setTouchedSets] = useState({});

  // Load stylesheets
  useEffect(() => {
    const link1 = document.createElement('link');
    link1.rel = 'stylesheet';
    link1.href = cssUrl('newuseronboarding.css');
    document.head.appendChild(link1);

    const link2 = document.createElement('link');
    link2.rel = 'stylesheet';
    link2.href = cssUrl('filterTemplate.css');
    document.head.appendChild(link2);

    return () => {
      document.head.removeChild(link1);
      document.head.removeChild(link2);
    };
  }, []);

  // Fetch sites and cleaners
  useEffect(() => {
    if (hasFetched.current) return;

    const fetchLists = async () => {
      try {
        setIsLoading(true);
        const [sitesResponse, cleanersResponse] = await Promise.all([
          fetch('/my/sites'),
          fetch('/api/cleaners'),
        ]);
        const [sitesData, cleanersData] = await Promise.all([
          sitesResponse.json(),
          cleanersResponse.json(),
        ]);
        setSitesList(Array.isArray(sitesData) ? sitesData : []);
        setCleanersList(Array.isArray(cleanersData) ? cleanersData : []);
      } catch (error) {
        console.error('Error fetching lists:', error);
        setSitesList([]);
        setCleanersList([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLists();
    hasFetched.current = true;
  }, []);

  // Toast auto-dismiss
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // --- Field helpers ---
  const setTouched = (formId, field) => {
    setTouchedSets((prev) => ({
      ...prev,
      [formId]: new Set([...(prev[formId] || []), field]),
    }));
  };

  const isTouched = (formId, field) => {
    return touchedSets[formId]?.has(field) || false;
  };

  const handleInputChange = (id, field, value) => {
    setFormSets((prev) =>
      prev.map((fs) => (fs.id === id ? { ...fs, [field]: value } : fs))
    );
    setTouched(id, field);
  };

  const handleNumberChange = (id, field, e) => {
    const val = e.target.value.replace(/[^0-9.]/g, '');
    handleInputChange(id, field, val);
  };

  // --- Date handler with admin-only prior-month gate ---
  const handleDateChange = (id, value) => {
    if (!value) {
      handleInputChange(id, 'dateWorked', '');
      return;
    }


    // Prior-month gate (non-admin only) — clear this record's date, leave others alone
    if (!isAdmin && isPriorMonth(value)) {
      setToast({ type: 'error', message: 'Cannot record extra work for a prior month' });
      setFormSets((prev) =>
        prev.map((fs) => (fs.id === id ? { ...fs, dateWorked: '' } : fs))
      );
      setTouched(id, 'dateWorked');
      return;
    }

    handleInputChange(id, 'dateWorked', value);
  };

  // --- Validation ---
  const validateForm = () => {
    const errors = [];
    formSets.forEach((set, index) => {
      if (!set.dateWorked?.trim()) errors.push(`Record ${index + 1}: Date is required`);
      if (!set.siteWorking?.trim()) errors.push(`Record ${index + 1}: Site is required`);
      if (!set.cleaner?.trim()) errors.push(`Record ${index + 1}: Cleaner is required`);
      if (!set.dayworked) errors.push(`Record ${index + 1}: Please select day worked`);
      if (!set.typeOfWork) errors.push(`Record ${index + 1}: Please select type of work`);
      if (!set.chargeable) errors.push(`Record ${index + 1}: Please specify if chargeable`);
      if (!set.supervisor) errors.push(`Record ${index + 1}: Please select approved by`);
      if (!set.hoursWorked?.toString().trim()) {
        errors.push(`Record ${index + 1}: Hours worked is required`);
      } else {
        const hours = parseFloat(set.hoursWorked);
        if (isNaN(hours) || hours <= 0 || hours > 24) {
          errors.push(`Record ${index + 1}: Hours must be a number between 0 and 24`);
        }
      }
      // Belt-and-braces: catch a prior-month date that somehow slipped through
      if (!isAdmin && set.dateWorked && isPriorMonth(set.dateWorked)) {
        errors.push(`Record ${index + 1}: Cannot record extra work for a prior month`);
      }
    });
    return errors;
  };
const toNumberString = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const cleaned = String(val).replace(/[$,\s]/g, '');
    if (cleaned === '' || cleaned === '-') return '';
    const num = parseFloat(cleaned);
    return isNaN(num) ? '' : String(num);
  };
  // --- Submit ---
  const handleSubmit = async () => {
    const errors = validateForm();
    setValidationErrors(errors);
    if (errors.length > 0) {
      setToast({ type: 'error', message: 'Please fix all errors before submitting' });
      return;
    }
    setIsSubmitting(true);
    try {

      const payload = formSets.map((fs) => ({
      ...fs,
      hoursWorked: toNumberString(fs.hoursWorked),
    }));

      const response = await fetch('/api/submit-extrawork', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formSets),
      });
      const data = await response.json();
      if (response.ok) {
        setToast({ type: 'success', message: 'Records saved successfully!' });
        setValidationErrors([]);
        setTimeout(() => navigate('/profile'), 2000);
      } else {
        throw new Error(data.message || 'Submission failed');
      }
    } catch (error) {
      setToast({ type: 'error', message: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addNewFormSet = () => {
    setFormSets((prev) => [...prev, createEmptyFormSet()]);
  };

  const deleteFormSet = (idToDelete) => {
    if (formSets.length <= 1) {
      setToast({ type: 'error', message: 'Cannot delete the last record' });
      return;
    }
    setFormSets((prev) => prev.filter((fs) => fs.id !== idToDelete));
  };

  const handleBack = () => {
    window.history.back();
  };

  // --- Input class helper ---
  const inputClass = (formId, field) => {
    const hasError = isTouched(formId, field) && !formSets.find((f) => f.id === formId)?.[field]?.trim();
    return `ob-input ${hasError ? 'ob-input-error' : ''}`;
  };

  // --- Loading state ---
  if (isLoading) {
    return (
      <div className="ob-page">
        <div className="ob-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <div className="fr-loading-state">
            <div className="fr-spinner" />
            <div className="fr-loading-text">Loading Extra Work Form…</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ob-page">
      {/* Toast */}
      {toast && (
        <div className={`ob-toast ${toast.type === 'success' ? 'ob-toast-success' : 'ob-toast-error'}`}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {toast.type === 'success' ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            )}
          </svg>
          {toast.message}
        </div>
      )}

      <div className="ob-container">
        {/* Header */}
        <div className="ob-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
          <h1 className="ob-title">Extra Work</h1>
          <p className="ob-subtitle">Fill in all fields to submit extra work records</p>
        </div>
        <HelpButton helpData={[
            { chip: 'extraworknew', urlFilter: '/static/pdffilters/ExtraWorkNew.pdf' }
          ]} />

        </div>

        {pdfUrl && (
          <ContractViewer pdfUrl={pdfUrl} onClose={() => setPdfUrl(null)} />
        )}
        {/* Form Sets */}
        {formSets.map((formSet, index) => (
          <div key={formSet.id} className="ob-form-card" style={{ marginBottom: '20px' }}>
            {/* Card Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}>
              <div>
                <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#f1f5f9' }}>
                  Record {index + 1}
                </span>
                {formSet.cleaner && (
                  <span style={{ fontSize: '0.8rem', color: '#22d3ee', marginLeft: '8px' }}>
                    — {formSet.cleaner}'s Extra Work
                  </span>
                )}
              </div>
              <button
                onClick={() => deleteFormSet(formSet.id)}
                style={{
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.3)',
                  borderRadius: '8px',
                  color: '#f87171',
                  padding: '6px 12px',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                Delete
              </button>
            </div>

            <div className="ob-form-fields">
              {/* Date Worked */}
              <div className="ob-field">
                <label className="ob-label">
                  Date Worked <span className="ob-label-required">*</span>
                </label>
                <input
                  type="date"
                  value={formSet.dateWorked}
                  onChange={(e) => handleDateChange(formSet.id, e.target.value)}
                  className={inputClass(formSet.id, 'dateWorked')}
                />
                {isTouched(formSet.id, 'dateWorked') && !formSet.dateWorked && (
                  <div className="ob-error">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Date is required
                  </div>
                )}
              </div>

              {/* Site Working - CustomSelect */}
              <div className="ob-field">
                <CustomSelect
                  label="Site Working"
                  value={formSet.siteWorking}
                  onChange={(val) => handleInputChange(formSet.id, 'siteWorking', val)}
                  options={sitesList}
                />
              </div>

              {/* Cleaner - CustomSelect */}
              <div className="ob-field">
                <CustomSelect
                  label="Cleaner Worked"
                  value={formSet.cleaner}
                  onChange={(val) => handleInputChange(formSet.id, 'cleaner', val)}
                  options={cleanersList}
                />
              </div>

              {/* Day Worked */}
              <div className="ob-field">
                <CustomSelect
                  label="Day Worked"
                  value={formSet.dayworked}
                  onChange={(val) => handleInputChange(formSet.id, 'dayworked', val)}
                  options={WEEKDAY_OPTIONS}
                />
              </div>

              {/* Type of Work */}
              <div className="ob-field">
                <CustomSelect
                  label="Type of Work"
                  value={formSet.typeOfWork}
                  onChange={(val) => handleInputChange(formSet.id, 'typeOfWork', val)}
                  options={WORK_TYPE_OPTIONS}
                />
              </div>

              {/* Chargeable */}
              <div className="ob-field">
                <CustomSelect
                  label="Chargeable"
                  value={formSet.chargeable}
                  onChange={(val) => handleInputChange(formSet.id, 'chargeable', val)}
                  options={CHARGEABLE_OPTIONS}
                />
              </div>

              {/* Hours Worked */}
              <div className="ob-field">
                <label className="ob-label">
                  Hours Worked
                </label>
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  inputMode="decimal"
                  value={formSet.hoursWorked}
                  onChange={(e) => handleNumberChange(formSet.id, 'hoursWorked', e)}
                  placeholder="0.00"
                  className={inputClass(formSet.id, 'hoursWorked')}
                />
              </div>

              {/* Approved By */}
              <div className="ob-field">
                <CustomSelect
                  label="Approved By"
                  value={formSet.supervisor}
                  onChange={(val) => handleInputChange(formSet.id, 'supervisor', val)}
                  options={TEAM_LEADER_OPTIONS}
                />
              </div>
            </div>
          </div>
        ))}

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div style={{
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: '12px',
            padding: '14px 18px',
            marginBottom: '16px',
          }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#f87171', marginBottom: '8px' }}>
              Please fix the following errors:
            </div>
            {validationErrors.map((error, i) => (
              <div key={i} style={{ fontSize: '0.8rem', color: '#fca5a5', marginBottom: '4px', paddingLeft: '8px' }}>
                • {error}
              </div>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
          <button
            onClick={addNewFormSet}
            className="ob-btn"
            style={{
              background: 'rgba(34, 211, 238, 0.1)',
              border: '1px solid rgba(34, 211, 238, 0.3)',
              color: '#22d3ee',
              fontWeight: 600,
            }}
          >
            + Add Another Record
          </button>

          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="ob-btn ob-btn-submit"
          >
            {isSubmitting ? (
              <span className="ob-spinner">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Submitting…
              </span>
            ) : (
              'Submit All Records'
            )}
          </button>
        </div>

        <p className="ob-footer">Extra Work</p>
      </div>
    </div>
  );
};

export default Extrawork;